/**
 * The day list — 730 days, grouped by the schedule's periods.
 *
 * Dumb by design: it renders whatever `daily.json` and `storage.ts` currently
 * say and reports taps back up. It holds no puzzle state of its own.
 *
 * Cards are built once and cached by day number. Re-rendering after a win
 * touches only the two cards whose state actually changed, so returning from a
 * level never rebuilds a 730-element list.
 */

import type { DailyEntry } from "../../scripts/lib/types.ts";
import { getCompletedDays, getCurrentDay } from "../storage.ts";

export interface DaySelectHandlers {
  onSelect(entry: DailyEntry): void;
}

const CHECK_PATH =
  '<path d="M4 12.5l5 5L20 7" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>';

const LOCK_PATH =
  '<path d="M5 11V7a7 7 0 0 1 14 0v4" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>' +
  '<path d="M3 11h18v10H3z" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>';

export class DaySelect {
  private days: DailyEntry[] = [];
  private readonly cards = new Map<number, HTMLButtonElement>();
  private minDifficulty = 0;
  private maxDifficulty = 1;
  /** Days after this are tomorrow's puzzle and beyond — not out yet. */
  private todayDay = Infinity;

  constructor(
    private readonly grid: HTMLElement,
    private readonly handlers: DaySelectHandlers,
  ) {}

  setDays(days: DailyEntry[], todayDay: number): void {
    this.days = days;
    this.todayDay = todayDay;
    const diffs = days.map((d) => d.level.stats.difficulty);
    this.minDifficulty = Math.min(...diffs);
    this.maxDifficulty = Math.max(...diffs);
    this.cards.clear();
    this.render();
  }

  /** Repaint only the cards whose state can have changed. */
  refresh(): void {
    const completed = getCompletedDays();
    const current = getCurrentDay();
    for (const [day, card] of this.cards) {
      this.paint(card, day, completed.has(day), day === current);
    }
    this.scrollToCurrent();
  }

  private render(): void {
    const completed = getCompletedDays();
    const current = getCurrentDay();
    const nodes: Node[] = [];
    let lastPeriod = "";

    // Only days that have actually arrived. Rendering the remaining ~726 as
    // greyed-out locked cards made the list overwhelmingly about puzzles the
    // player cannot touch, and buried the handful they can. A period header
    // is only emitted once a visible day belongs to it, so no empty headings
    // survive the filter.
    for (const entry of this.days) {
      if (entry.day > this.todayDay) break;
      if (entry.period !== lastPeriod) {
        lastPeriod = entry.period;
        const header = document.createElement("h2");
        header.className = "period-header";
        header.textContent = entry.period;
        nodes.push(header);
      }
      const card = this.buildCard(entry);
      this.paint(card, entry.day, completed.has(entry.day), entry.day === current);
      this.cards.set(entry.day, card);
      nodes.push(card);
    }

    this.grid.replaceChildren(...nodes);
    this.scrollToCurrent();
  }

  private scrollToCurrent(): void {
    const current = getCurrentDay();
    if (current === null) return;
    const card = this.cards.get(current);
    if (!card) return;
    // Scroll our own container, never scrollIntoView. That walks every ancestor
    // scroll container, and the game is embedded in an iframe near the foot of
    // dailyfbb.com -- so centring a card here dragged the whole host page down
    // to the iframe on load. Setting scrollTop cannot escape this element.
    // Rects rather than offsetTop, which is relative to the offsetParent and
    // only happens to agree while .day-grid is unpositioned.
    const grid = this.grid.getBoundingClientRect();
    const target = card.getBoundingClientRect();
    this.grid.scrollTop += target.top - grid.top - (grid.height - target.height) / 2;
  }

  private buildCard(entry: DailyEntry): HTMLButtonElement {
    const card = document.createElement("button");
    card.type = "button";
    card.className = "day-card";

    const day = document.createElement("span");
    day.className = "day-card-number";
    day.textContent = String(entry.day);

    const size = document.createElement("span");
    size.className = "day-card-size";
    size.textContent = `${entry.level.rows}×${entry.level.cols}`;

    const track = document.createElement("span");
    track.className = "day-card-track";
    const fill = document.createElement("span");
    fill.className = "day-card-fill";
    const span = Math.max(1, this.maxDifficulty - this.minDifficulty);
    const pct = ((entry.level.stats.difficulty - this.minDifficulty) / span) * 100;
    fill.style.width = `${Math.max(6, Math.round(pct))}%`;
    track.appendChild(fill);

    card.append(day, size, track);
    card.addEventListener("click", () => this.handlers.onSelect(entry));
    return card;
  }

  private paint(card: HTMLButtonElement, day: number, done: boolean, isCurrent: boolean): void {
    const locked = day > this.todayDay;
    card.classList.toggle("day-card-done", done);
    card.classList.toggle("day-card-current", isCurrent);
    card.classList.toggle("day-card-locked", locked);
    card.disabled = locked;
    card.setAttribute("aria-disabled", String(locked));

    // Top-right badge: a lock beats a checkmark beats nothing — a locked day
    // is never done, but keep the precedence explicit rather than assumed.
    let badge = card.querySelector(".day-card-badge");
    const wantIcon = locked ? "lock" : done ? "check" : null;
    const haveIcon = badge?.getAttribute("data-icon") ?? null;
    if (wantIcon !== haveIcon) {
      badge?.remove();
      badge = null;
    }
    if (wantIcon && !badge) {
      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("viewBox", "0 0 24 24");
      svg.setAttribute("class", `day-card-badge day-card-badge-${wantIcon}`);
      svg.setAttribute("data-icon", wantIcon);
      svg.setAttribute("aria-hidden", "true");
      svg.innerHTML = wantIcon === "check" ? CHECK_PATH : LOCK_PATH;
      card.appendChild(svg);
    }

    card.setAttribute(
      "aria-label",
      `Day ${day}${locked ? ", not available yet" : done ? ", completed" : ""}${isCurrent ? ", last played" : ""}`,
    );
  }
}
